import { Link, Navigate } from 'react-router-dom'
import { useLeaveModuleEnabled } from './useLeaveModuleEnabled'
import './TeamPage.css'

export function TeamHelpPage() {
  const { enabled, isLoading } = useLeaveModuleEnabled()

  if (isLoading) return <p className="team-page">Loading Team…</p>
  if (!enabled) return <Navigate to="/dashboard" replace />

  return (
    <div className="team-page">
      <p className="team-help-back"><Link to="/team">Back to Team</Link></p>
      <h1>How to use Team</h1>
      <p className="team-intro">This is for everyone who looks at the team calendar or asks for their own leave.</p>
      <div className="team-help">
        <section>
          <h2>What can I do in Team?</h2>
          <p>You can see who is off, and you can ask for your own time off. You do not need a PIN to look at the calendar. You do need your PIN to see your own balance or to send a request.</p>
        </section>
        <section>
          <h2>How do I read the calendar?</h2>
          <ul>
            <li>Blue is annual leave.</li>
            <li>Yellow is sick leave.</li>
            <li>Green is study leave.</li>
            <li>Grey is other leave.</li>
            <li>A split colour is a half day.</li>
            <li>Purple is a public holiday. That day is not counted as leave.</li>
          </ul>
          <p>Today’s date is marked. The calendar shows leave that has been approved. A request that is still waiting does not appear there yet.</p>
        </section>
        <section>
          <h2>What is my PIN for?</h2>
          <p>Your PIN is 6 digits. The clinic gives it to you. Enter it under My leave to see your balance, email it to yourself, ask for leave, or change your PIN.</p>
          <p>On a shared computer, choose Leave this record when you are finished, so the next person does not see your balance.</p>
        </section>
        <section>
          <h2>How do I change my PIN?</h2>
          <p>Sign in with your current PIN. Under Change PIN, type a new 6-digit PIN, or choose Generate, then Save PIN. The new number is shown once. Use it the next time you sign in.</p>
        </section>
        <section>
          <h2>What do the balance numbers mean?</h2>
          <p>Numbers are shown to two decimal places.</p>
          <ul>
            <li>Annual leave is the days you can still take. It increases through the year. Days you do not use do not carry into next year.</li>
            <li>Sick leave is a set number of days for the year. It does not build up day by day. It is available after 13 weeks of work. Unused sick days do not carry into next year.</li>
            <li>Study leave is a set number of days for the year that starts on your start date. It does not build up day by day. Unused study days do not carry into the next year of your contract.</li>
          </ul>
        </section>
        <section>
          <h2>Which days count?</h2>
          <p>Only days you normally work. Weekends, public holidays, and your other days off are left out.</p>
          <p>For annual leave, a shorter working day counts as part of a full day. A morning you usually work for half of a full day counts as half a day of annual leave. You can also mark the first day, and the last day of a longer request, as a half day.</p>
        </section>
        <section>
          <h2>How do I ask for leave?</h2>
          <ol>
            <li>Sign in with your PIN.</li>
            <li>Under Request leave, choose the type.</li>
            <li>Choose the first day and the last day.</li>
            <li>Add a note if you want to.</li>
            <li>Choose Submit request.</li>
          </ol>
          <p>The request then waits for approval. If the clinic has your email address, you will get an email when it is approved or declined. You can also see the decision under Your requests.</p>
        </section>
        <section>
          <h2>What if I do not have enough annual leave?</h2>
          <p>You can only ask for as much annual leave as you have for those dates. If the request is too long, a message appears before you can send it. It tells you how many days the request is, and how many days you have.</p>
          <p>If you need more time off, choose Unpaid leave. On the calendar, that is shown as Leave.</p>
        </section>
        <section>
          <h2>What if sick leave or study leave will not submit?</h2>
          <p>You cannot ask for more sick leave or study leave than you have left. Study leave is only available from your start date. If the dates would cross the anniversary of your start date, send two requests: one up to that date, and one after it.</p>
          <p>A request also has to start and finish in the same calendar year. Ask for the next year separately. You cannot ask for a day you already have leave on.</p>
        </section>
        <section>
          <h2>Can I cancel a request?</h2>
          <p>Yes, while it is waiting for approval. You can also cancel approved leave before the first day. Once that leave has started, you cannot cancel it yourself. Speak to the clinic if it needs to change.</p>
        </section>
        <section>
          <h2>Why did I not get an email?</h2>
          <p>Emails are only sent when your record has an email address. You can still see what happened under Your requests after you sign in.</p>
        </section>
        <section>
          <h2>Why has my annual leave stopped going up?</h2>
          <p>Sometimes your remaining annual leave is set for the rest of this year. If your balance says no further annual leave accrues until 1 January, that figure is all you have left until the new year. It starts again in January.</p>
        </section>
        <p className="team-help-back"><Link to="/team">Back to Team</Link></p>
      </div>
    </div>
  )
}
